'use client';

import type { ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

interface EchoOverlayAnchorProps {
  /** The hero section content */
  children: ReactNode;
  /** The echo bot overlay to position at the hero's bottom-right */
  echoOverlay?: ReactNode;
  /**
   * Tailwind class applied to the mobile fixed overlay's bottom offset.
   * Defaults to `bottom-0`. The editor overrides this when its mobile
   * action footer is visible so the bot clears the footer bar.
   */
  mobileBottomClass?: string;
}

/** Gap between the viewport top and the chat container once pinned (desktop). */
const STICKY_TOP_GAP_PX = 16;
/** Avatar straddles the hero edge by half its height (72px / 2). */
const HERO_STRADDLE_OFFSET_PX = 36;
/** Breakpoint mirroring Tailwind's `md:` (desktop scroll behavior applies at/above). */
const DESKTOP_BREAKPOINT_PX = 768;

/**
 * Client wrapper that measures the hero section's bounding rect
 * and positions the echo overlay at its bottom-right corner.
 *
 * Desktop scroll behavior: the overlay starts anchored at the hero's
 * bottom-right (straddling the boundary). As the user scrolls, the
 * overlay moves up with the page. Once its top reaches a small gap
 * from the viewport top, it switches to `fixed` positioning so it
 * stays pinned while the page continues to scroll.
 */
export function EchoOverlayAnchor({
  children,
  echoOverlay,
  mobileBottomClass = 'bottom-0',
}: EchoOverlayAnchorProps) {
  const heroRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [heroHeight, setHeroHeight] = useState(0);
  const [isPinned, setIsPinned] = useState(false);
  const [pinnedRightPx, setPinnedRightPx] = useState(24);

  useEffect(() => {
    const element = heroRef.current;
    if (!element) return;

    const observer = new ResizeObserver(entries => {
      const entry = entries[0];
      if (entry) {
        setHeroHeight(entry.contentRect.height);
      }
    });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const hero = heroRef.current;
    const overlay = overlayRef.current;
    if (!hero || !overlay) return;

    let rafId = 0;

    const update = () => {
      rafId = 0;
      if (window.innerWidth < DESKTOP_BREAKPOINT_PX) {
        setIsPinned(false);
        return;
      }
      const overlayHeight = overlay.offsetHeight;
      const heroRect = hero.getBoundingClientRect();
      // Y coordinate where the overlay's top sits when anchored to the hero
      // (hero bottom + straddle offset - overlay height).
      const anchoredTopY =
        heroRect.bottom + HERO_STRADDLE_OFFSET_PX - overlayHeight;
      setIsPinned(anchoredTopY <= STICKY_TOP_GAP_PX);
      // Measure the distance from the viewport's right edge to the hero's
      // right edge. The hero shares its right edge with the content column
      // (`max-w-[1232px] mx-auto`), so this is the offset we need to match
      // when switching to `position: fixed` — otherwise `right-6` would
      // measure from the viewport edge and shove the overlay off-column
      // on viewports wider than 1232px.
      const containerRight = window.innerWidth - heroRect.right;
      setPinnedRightPx(containerRight + 24);
    };

    const onScrollOrResize = () => {
      if (rafId !== 0) return;
      rafId = window.requestAnimationFrame(update);
    };

    update();
    window.addEventListener('scroll', onScrollOrResize, { passive: true });
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      if (rafId !== 0) window.cancelAnimationFrame(rafId);
      window.removeEventListener('scroll', onScrollOrResize);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [heroHeight]);

  return (
    <div className="relative">
      {/* Hero content — measured via ref */}
      <div ref={heroRef}>{children}</div>

      {/* Echo overlay.
          - Mobile: fixed to viewport bottom-right so the bot stays visible
            while the user scrolls the page.
          - Desktop: starts absolutely anchored to the hero's bottom edge —
            offset down by half the avatar height (36px) so the avatar
            straddles the hero/content boundary. On scroll, the overlay moves
            with the page; once its top would cross the viewport's top gap,
            it switches to `fixed` and pins there. Bubbles + input stack
            upward from the avatar regardless of state. */}
      {echoOverlay && heroHeight > 0 && (
        <div
          ref={overlayRef}
          // z-[9999] keeps the bot above full-screen image/gallery viewers
          // (lightboxes typically sit in the z-50..z-[100] range) so users
          // can still interact with the assistant while a viewer is open.
          className={
            isPinned
              ? `pointer-events-none fixed right-4 z-[9999] ${mobileBottomClass} md:bottom-auto md:left-auto`
              : `pointer-events-none fixed right-4 z-[9999] ${mobileBottomClass} md:absolute md:right-6 md:-bottom-9`
          }
          style={
            isPinned
              ? {
                  top: `${STICKY_TOP_GAP_PX}px`,
                  // Applied as an inline style so it only activates at md+
                  // via the min-width check in `update()` (which only
                  // toggles `isPinned` on desktop). On mobile the className
                  // still carries `right-4` and this value is ignored
                  // because the mobile overlay never enters pinned mode.
                  right: `${pinnedRightPx}px`,
                }
              : undefined
          }
        >
          <div className="pointer-events-auto">{echoOverlay}</div>
        </div>
      )}
    </div>
  );
}
